import React from 'react';
import { useApolloClient } from '@apollo/client';

import { messageFromError } from '@oysterjs/core/graphql/errors';
import { Spinner } from '@oysterjs/ui/spinner';

import { runAmsReconciliation } from '@oysterjs/ais/api/data/ams-reconciliation/mutations';
import {
  type AmsCodeListFieldsFragment,
  type AmsFieldVerdictFieldsFragment,
  type AmsReconciliationLineOfBusiness,
  type AmsReconciliationSourceInput,
  type AmsReconciliationTargetFieldsFragment
} from '@oysterjs/ais/api/graphql';
import { destinationLines } from './destinations';

/**
 * A destination a run can execute against. The picker also lists records with no
 * canonical schema; those carry no line of business, and this type keeps them out.
 */
export type ReconcilableTarget = AmsReconciliationTargetFieldsFragment & {
  lineOfBusiness: AmsReconciliationLineOfBusiness;
};

/** One destination's findings, kept alongside the destination they describe. */
export type DestinationFindings = {
  target: ReconcilableTarget;
  verdicts: AmsFieldVerdictFieldsFragment[];
  /** The AMS's lists for the coded fields among the verdicts, by optionsId. */
  codeLists?: AmsCodeListFieldsFragment[];
  /** Why this destination's pass failed, in which case it has no verdicts. */
  failure?: string;
};

const UNREAD = "Couldn't compare this destination with Applied Epic.";

/**
 * Step two: compare the document against every chosen destination, one call each
 * because each line of business extracts against its own schema. The calls run
 * concurrently, so the wait is the slowest rather than the sum. Destinations are
 * separate Epic records, so one failing still leaves the others to review; only when
 * every one fails is there nothing to show.
 */
export function ReconciliationPipeline(props: {
  source: AmsReconciliationSourceInput;
  targets: ReconcilableTarget[];
  onComplete: (findings: DestinationFindings[]) => void;
  onError: (failures: DestinationFindings[]) => void;
}) {
  const client = useApolloClient();

  // The run lives in a ref and the effect only attaches to it. StrictMode mounts,
  // cleans up and remounts, so a start-once guard would leave the first run's result
  // discarded with nothing listening, and restarting would repeat the extraction.
  const run = React.useRef<Promise<DestinationFindings[]> | null>(null);
  const callbacks = React.useRef({ onComplete: props.onComplete, onError: props.onError });
  callbacks.current = { onComplete: props.onComplete, onError: props.onError };

  const { source, targets } = props;
  React.useEffect(() => {
    let detached = false;

    run.current ??= Promise.allSettled(
      targets.map(async (target) => {
        const { data, error } = await runAmsReconciliation(
          {
            policyId: target.policyId ?? '',
            source,
            lineOfBusiness: target.lineOfBusiness
          },
          client
        );
        if (error) throw error;
        if (!data) {
          throw new Error('runAmsReconciliation returned no data');
        }
        return {
          target,
          verdicts: data.verdicts,
          codeLists: data.codeLists
        };
      })
    ).then((results) =>
      results.map((result, index) =>
        result.status === 'fulfilled'
          ? result.value
          : {
              target: targets[index],
              verdicts: [],
              failure: messageFromError(result.reason, UNREAD)
            }
      )
    );

    void run.current.then((findings) => {
      if (detached) {
        return;
      }
      if (findings.every((destination) => destination.failure)) {
        callbacks.current.onError(findings);
      } else {
        callbacks.current.onComplete(findings);
      }
    });

    return () => {
      detached = true;
    };
  }, [client, source, targets]);

  return (
    <div>
      {/* No heading: the dialog title already names this step. */}
      <div className="text-sm/6 text-neutral-500 dark:text-neutral-400">
        {destinationLines(targets).map((line) => (
          <p key={line} className="m-0">
            {line}
          </p>
        ))}
      </div>

      <div className="mt-6 flex items-center gap-3">
        <Spinner size="size-4" color="sky" />
        <p className="m-0 text-sm/6 text-neutral-500 dark:text-neutral-400">
          Reading the document and comparing it with Applied Epic
        </p>
      </div>
    </div>
  );
}
